import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlaylistAppearanceSettings } from '@/store/playlist/types';

interface PlaylistHeaderProps {
  playlistName: string;
  headerTitle?: string;
  favicon?: string;
  creatorName?: string;
  customLogo?: string;
  externalButton?: { text: string; url: string };
  appearanceSettings?: PlaylistAppearanceSettings;
}

// Função para calcular se o texto deve ser claro ou escuro baseado na cor de fundo
const getTextColor = (bgColor: string): string => {
  // Remove # se existir
  const hex = bgColor.replace('#', '');
  
  // Converte para RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calcula luminosidade
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Retorna branco para fundos escuros, preto para fundos claros
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export const PlaylistHeader: React.FC<PlaylistHeaderProps> = ({
  playlistName,
  headerTitle,
  favicon,
  creatorName,
  customLogo,
  externalButton,
  appearanceSettings,
}) => {
  useLanguage();
  const headerColor = appearanceSettings?.headerColor || '#0A1A3A';
  const textColor = getTextColor(headerColor);
  const accentColor = appearanceSettings?.accentColor || '#2D6CF6';
  
  // Usa headerTitle se fornecido, caso contrário usa playlistName
  const displayTitle = headerTitle || playlistName;

  return (
    <header 
      className="border-b" 
      style={{ backgroundColor: headerColor }}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {customLogo ? (
              <img 
                src={customLogo} 
                alt={translateInterfaceText("Logo")}
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ 
                  backgroundColor: `${accentColor}20`,
                }}
              >
                <span 
                  className="text-lg font-bold"
                  style={{ color: accentColor }}
                >
                  H
                </span>
              </div>
            )}
            
            <div className="min-w-0 flex-1 flex items-center gap-2">
              {favicon && (
                <img 
                  src={favicon} 
                  alt={translateInterfaceText("Favicon")}
                  className="w-5 h-5 flex-shrink-0 object-contain"
                />
              )}
              <h1 
                className="font-semibold text-lg truncate"
                style={{ color: textColor }}
              >
                {displayTitle}
              </h1>
            </div>
          </div>
          
          {externalButton && (
            <Button 
              style={{ 
                backgroundColor: accentColor,
                color: '#FFFFFF',
              }}
              className="hover:opacity-90 transition-opacity"
              onClick={() => window.open(externalButton.url, '_blank')}
            >
              {externalButton.text}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
